import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { apiUrl } from "../../../utils/config";
import {
  Plus, Edit2, Trash2, X, Briefcase, Users, Eye,
  MapPin, Clock, DollarSign, CheckCircle, XCircle, AlertCircle,
} from "lucide-react";

const API = apiUrl;

const JOB_TYPES   = ["Full-time", "Part-time", "Contract", "Internship", "Remote"];
const DEPARTMENTS = ["Kitchen", "Service", "Management", "Delivery", "Marketing", "IT", "HR", "Finance", "Other"];
const STATUS_OPTS = ["Open", "Paused", "Closed"];

const APP_STATUS_COLORS = {
  pending:   "bg-yellow-100 text-yellow-700",
  reviewed:  "bg-blue-100 text-blue-700",
  shortlisted: "bg-purple-100 text-purple-700",
  rejected:  "bg-red-100 text-red-700",
  hired:     "bg-green-100 text-green-700",
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

// ── Confirm Delete Dialog ─────────────────────────────────────
const ConfirmDialog = ({ message, onConfirm, onCancel }) => (
  <>
    <div className="fixed inset-0 bg-black/40 z-50 backdrop-blur-sm" />
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <AlertCircle size={20} className="text-red-500" />
          </div>
          <p className="text-sm text-gray-700 font-medium">{message}</p>
        </div>
        <div className="flex gap-3 justify-end">
          <button onClick={onCancel} className="px-4 py-2 text-sm rounded-xl border border-gray-200 hover:bg-gray-50 font-semibold transition">Cancel</button>
          <button onClick={onConfirm} className="px-4 py-2 text-sm rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold transition">Delete</button>
        </div>
      </div>
    </div>
  </>
);

// ── Application Detail Drawer ─────────────────────────────────
const AppDrawer = ({ app, onClose, onStatusChange }) => {
  const [status, setStatus] = useState(app.status || "pending");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/careers/applications/${app.id}`, { status });
      onStatusChange(app.id, status);
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to update status.";
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white z-50 shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-lg font-extrabold text-gray-900">Application Details</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition"><X size={18} className="text-gray-500" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Applicant Info */}
          <div className="bg-orange-50 rounded-2xl p-4 space-y-2">
            <h3 className="font-extrabold text-gray-900 text-base">{app.applicant_name}</h3>
            <p className="text-sm text-gray-600">{app.applicant_email}</p>
            {app.applicant_phone && <p className="text-sm text-gray-600">{app.applicant_phone}</p>}
            <p className="text-xs text-gray-400">Applied {fmtDate(app.created_at)}</p>
          </div>

          {/* Job Applied For */}
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Position</p>
            <p className="text-sm font-semibold text-gray-800">{app.job_title || app.career?.title || "—"}</p>
          </div>

          {/* Cover Letter */}
          {app.cover_letter && (
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Cover Letter</p>
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap bg-gray-50 rounded-xl p-4 border border-gray-100">
                {app.cover_letter}
              </p>
            </div>
          )}

          {/* Resume Link */}
          {app.resume_url && (
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Resume</p>
              <a href={app.resume_url} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-orange-600 hover:underline font-medium">
                View Resume →
              </a>
            </div>
          )}

          {/* Status Update */}
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Update Status</p>
            <div className="grid grid-cols-2 gap-2">
              {Object.keys(APP_STATUS_COLORS).map((s) => (
                <button key={s} onClick={() => setStatus(s)}
                  className={`px-3 py-2 rounded-xl text-xs font-bold border-2 capitalize transition ${
                    status === s ? "border-orange-500 bg-orange-50 text-orange-700" : "border-gray-200 text-gray-500 hover:border-gray-300"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="p-5 border-t border-gray-100 flex gap-3">
          <button onClick={onClose} className="flex-1 py-3 border border-gray-200 rounded-xl text-sm font-semibold hover:bg-gray-50 transition">Close</button>
          <button onClick={save} disabled={saving || status === app.status}
            className="flex-1 py-3 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl transition disabled:opacity-50">
            {saving ? "Saving…" : "Save Status"}
          </button>
        </div>
      </div>
    </>
  );
};

// ── Job Form Modal ────────────────────────────────────────────
const INITIAL_FORM = {
  title: "", department: "Kitchen", location: "", job_type: "Full-time",
  salary_range: "", description: "", requirements: "", status: "Open",
};

const JobModal = ({ job, onClose, onSaved }) => {
  const [form, setForm] = useState(job
    ? { title: job.title, department: job.department, location: job.location, job_type: job.job_type,
        salary_range: job.salary_range || "", description: job.description, requirements: job.requirements || "", status: job.status }
    : INITIAL_FORM
  );
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");

  const h = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) return;
    setSaving(true);
    setError("");
    try {
      let saved;
      if (job) {
        const res = await axios.put(`${API}/careers/${job.id}`, form);
        // handle: { career }, { data }, { job }, or direct object
        saved = res.data.career ?? res.data.data ?? res.data.job ?? res.data;
        if (!saved?.id) saved = { ...job, ...form };
      } else {
        const res = await axios.post(`${API}/careers`, form);
        saved = res.data.career ?? res.data.data ?? res.data.job ?? res.data;
        if (!saved?.id) saved = { id: Date.now(), ...form, created_at: new Date().toISOString(), applications_count: 0 };
      }
      onSaved(saved);
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || "Failed to save. Please try again.";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex justify-between items-center p-5 border-b border-gray-100">
          <h2 className="text-xl font-extrabold text-gray-900">{job ? "Edit Job" : "New Job Listing"}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition"><X size={20} className="text-gray-500" /></button>
        </div>
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-4">
          {error && <p className="text-sm text-red-500 bg-red-50 rounded-xl px-4 py-2">{error}</p>}

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1.5">Job Title *</label>
            <input required name="title" value={form.title} onChange={h} placeholder="e.g. Head Chef" className={inputCls} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1.5">Department</label>
              <select name="department" value={form.department} onChange={h} className={inputCls}>
                {DEPARTMENTS.map((d) => <option key={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1.5">Job Type</label>
              <select name="job_type" value={form.job_type} onChange={h} className={inputCls}>
                {JOB_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1.5">Location</label>
              <input name="location" value={form.location} onChange={h} placeholder="e.g. Dhaka, Bangladesh" className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1.5">Salary Range</label>
              <input name="salary_range" value={form.salary_range} onChange={h} placeholder="e.g. ৳30,000 – ৳50,000" className={inputCls} />
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1.5">Status</label>
            <select name="status" value={form.status} onChange={h} className={`${inputCls} w-40`}>
              {STATUS_OPTS.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1.5">Job Description *</label>
            <textarea required name="description" value={form.description} onChange={h} rows={5}
              placeholder="Describe the role, responsibilities, and what a typical day looks like…"
              className={`${inputCls} resize-none`} />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1.5">Requirements</label>
            <textarea name="requirements" value={form.requirements} onChange={h} rows={4}
              placeholder="Skills, experience, qualifications needed…"
              className={`${inputCls} resize-none`} />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold hover:bg-gray-50 transition">Cancel</button>
            <button type="submit" disabled={saving}
              className="px-5 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold shadow-lg shadow-orange-200 transition active:scale-95 disabled:opacity-60">
              {saving ? "Saving…" : job ? "Save Changes" : "Post Job"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Main Admin Careers Page ───────────────────────────────────
const CareersAdminPage = () => {
  const [tab, setTab]           = useState("listings"); // "listings" | "applications"

  // Jobs state
  const [jobs, setJobs]         = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [jobModal, setJobModal] = useState(null);     // null | "new" | job object
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Applications state
  const [apps, setApps]         = useState([]);
  const [loadingApps, setLoadingApps] = useState(false);
  const [appsLoaded, setAppsLoaded]   = useState(false);
  const [appDrawer, setAppDrawer]     = useState(null);
  const [appFilter, setAppFilter]     = useState("all");

  // ── Load Jobs ──────────────────────────────────────────────
  const loadJobs = useCallback(async () => {
    setLoadingJobs(true);
    try {
      const res = await axios.get(`${API}/careers`);
      setJobs(res.data.careers ?? res.data.list ?? res.data.data ?? (Array.isArray(res.data) ? res.data : []));
    } catch (err) {
      console.error("Failed to load jobs:", err?.response?.data || err?.message);
      setJobs([]);
    } finally {
      setLoadingJobs(false);
    }
  }, []);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  // ── Load Applications (lazy — only when tab visited) ──────
  const loadApps = useCallback(async () => {
    setLoadingApps(true);
    try {
      const res = await axios.get(`${API}/careers/applications`);
      setApps(res.data.applications ?? res.data.list ?? res.data.data ?? (Array.isArray(res.data) ? res.data : []));
      setAppsLoaded(true);
    } catch (err) {
      console.error("Failed to load applications:", err?.response?.data || err?.message);
      setApps([]);
      setAppsLoaded(true);
    } finally {
      setLoadingApps(false);
    }
  }, []);

  useEffect(() => {
    if (tab === "applications" && !appsLoaded) loadApps();
  }, [tab, appsLoaded, loadApps]);

  // ── Job CRUD ───────────────────────────────────────────────
  const handleJobSaved = (saved) => {
    setJobs((prev) => {
      const idx = prev.findIndex((j) => j.id === saved.id);
      if (idx !== -1) { const next = [...prev]; next[idx] = saved; return next; }
      return [saved, ...prev];
    });
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/careers/${deleteTarget.id}`);
      setJobs((prev) => prev.filter((j) => j.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to delete. Please try again.";
      alert(msg);
    } finally {
      setDeleting(false);
    }
  };

  const handleAppStatusChange = (id, newStatus) => {
    setApps((prev) => prev.map((a) => a.id === id ? { ...a, status: newStatus } : a));
  };

  // ── Derived stats ──────────────────────────────────────────
  const openJobs   = jobs.filter((j) => j.status === "Open").length;
  const pausedJobs = jobs.filter((j) => j.status === "Paused").length;
  const totalApps  = apps.length;
  const pendingApps = apps.filter((a) => a.status === "pending").length;

  const filteredApps = appFilter === "all" ? apps : apps.filter((a) => a.status === appFilter);

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Careers & Recruitment</h1>
          <p className="text-gray-500 text-sm">Manage job listings and review applicants.</p>
        </div>
        {tab === "listings" && (
          <button onClick={() => setJobModal("new")}
            className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-xl font-bold shadow-lg shadow-orange-200 flex items-center gap-2 transition active:scale-95">
            <Plus size={20} /> Post a Job
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: "Total Jobs",    value: jobs.length,   icon: Briefcase,    color: "text-orange-500 bg-orange-50" },
          { label: "Open",          value: openJobs,       icon: CheckCircle,  color: "text-green-600 bg-green-50"  },
          { label: "Paused",        value: pausedJobs,     icon: XCircle,      color: "text-yellow-600 bg-yellow-50" },
          { label: "Applications",  value: appsLoaded ? totalApps : "—", icon: Users, color: "text-blue-600 bg-blue-50" },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${s.color}`}>
              <s.icon size={20} />
            </div>
            <div>
              <p className="text-2xl font-extrabold text-gray-900">{s.value}</p>
              <p className="text-xs text-gray-500">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Tab Switch */}
      <div className="flex gap-1 bg-gray-100 rounded-2xl p-1 w-fit">
        {[["listings", "Job Listings"], ["applications", "Applications"]].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`px-5 py-2 text-sm font-bold rounded-xl transition-all ${
              tab === key ? "bg-white shadow text-orange-600" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {label}
            {key === "applications" && appsLoaded && pendingApps > 0 && (
              <span className="ml-2 bg-orange-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                {pendingApps}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── JOB LISTINGS TAB ─────────────────────────────────── */}
      {tab === "listings" && (
        <div className="space-y-3">
          {loadingJobs ? (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center text-gray-400 text-sm">
              Loading jobs…
            </div>
          ) : jobs.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center">
              <Briefcase size={36} className="mx-auto mb-3 text-gray-300" />
              <p className="text-gray-400 text-sm">No job listings yet. Post your first role.</p>
            </div>
          ) : (
            jobs.map((job) => (
              <div key={job.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="font-bold text-gray-900 text-base">{job.title}</h3>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      job.status === "Open"   ? "bg-green-100 text-green-700" :
                      job.status === "Paused" ? "bg-yellow-100 text-yellow-700" :
                                                "bg-gray-100 text-gray-500"
                    }`}>{job.status}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1"><Briefcase size={12} />{job.department}</span>
                    {job.location && <span className="flex items-center gap-1"><MapPin size={12} />{job.location}</span>}
                    <span className="flex items-center gap-1"><Clock size={12} />{job.job_type}</span>
                    {job.salary_range && <span className="flex items-center gap-1"><DollarSign size={12} />{job.salary_range}</span>}
                  </div>
                  {job.description && (
                    <p className="mt-2 text-xs text-gray-400 line-clamp-2">{job.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {job.applications_count !== undefined && (
                    <span className="flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-3 py-1.5 rounded-lg">
                      <Users size={12} /> {job.applications_count}
                    </span>
                  )}
                  <button onClick={() => setJobModal(job)} className="p-2 hover:bg-orange-50 rounded-xl text-orange-500 transition"><Edit2 size={16} /></button>
                  <button onClick={() => setDeleteTarget(job)} className="p-2 hover:bg-red-50 rounded-xl text-red-400 transition"><Trash2 size={16} /></button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ── APPLICATIONS TAB ──────────────────────────────────── */}
      {tab === "applications" && (
        <div className="space-y-4">
          {/* Filter */}
          <div className="flex flex-wrap gap-2">
            {["all", ...Object.keys(APP_STATUS_COLORS)].map((f) => (
              <button key={f} onClick={() => setAppFilter(f)}
                className={`px-4 py-1.5 text-xs font-bold rounded-xl capitalize border transition ${
                  appFilter === f ? "bg-orange-500 border-orange-500 text-white" : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          {loadingApps ? (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center text-gray-400 text-sm">Loading applications…</div>
          ) : filteredApps.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center">
              <Users size={36} className="mx-auto mb-3 text-gray-300" />
              <p className="text-gray-400 text-sm">No applications {appFilter !== "all" ? `with status "${appFilter}"` : "yet"}.</p>
            </div>
          ) : (
            filteredApps.map((app) => (
              <div key={app.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-bold text-sm flex-shrink-0">
                  {app.applicant_name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold text-gray-900 text-sm">{app.applicant_name}</p>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${APP_STATUS_COLORS[app.status] || "bg-gray-100 text-gray-500"}`}>
                      {app.status}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 truncate">{app.applicant_email}</p>
                  <p className="text-xs text-gray-400 mt-0.5">For: <span className="font-medium text-gray-600">{app.job_title || app.career?.title || "—"}</span> · {fmtDate(app.created_at)}</p>
                </div>
                <button onClick={() => setAppDrawer(app)}
                  className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 hover:bg-orange-50 rounded-xl text-xs font-bold text-gray-600 hover:text-orange-600 transition flex-shrink-0">
                  <Eye size={14} /> View
                </button>
              </div>
            ))
          )}
        </div>
      )}

      {/* ── Modals / Drawers ──────────────────────────────────── */}
      {jobModal && (
        <JobModal
          job={jobModal === "new" ? null : jobModal}
          onClose={() => setJobModal(null)}
          onSaved={handleJobSaved}
        />
      )}

      {deleteTarget && (
        <ConfirmDialog
          message={`Delete "${deleteTarget.title}"? This will also remove all applications for this role.`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {appDrawer && (
        <AppDrawer
          app={appDrawer}
          onClose={() => setAppDrawer(null)}
          onStatusChange={handleAppStatusChange}
        />
      )}
    </div>
  );
};

export default CareersAdminPage;
